import { Suspense, useEffect, useState } from 'react';
import React from 'react';
import { ResetPassword } from './components/auth/ResetPassword';

// Contexts & Services
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './services/auth';
import { FirebaseAuthProvider } from './contexts/FirebaseAuthContext';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import { subscribe } from './services/bus';
import { trackGoal } from './utils/analytics';
import { trackVisit } from './utils/tracker';

// Components
import { CookieBanner } from './components/ui/CookieBanner';
import { AppLayout } from './components/layout/AppLayout';
import { MobileRedirectHandler } from './components/layout/MobileRedirectHandler';
import { Paywall } from './components/layout/Paywall';

// Component to listen to bus events and invalidate queries
const QueryBusListener = () => {
  const qc = useQueryClient();
  useEffect(() => {
    const unsubscribe = subscribe((msg) => {
      if (msg.topic) {
        console.debug('[React Query] Invalidating via bus:', msg.topic);
        qc.invalidateQueries({ queryKey: [msg.topic] });

        if (msg.topic === 'waybills') {
          qc.invalidateQueries({ queryKey: ['vehicles'] });
        }
      }
    });
    return unsubscribe;
  }, [qc]);
  return null;
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-full w-full">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
  </div>
);

// SubscriptionGate - проверяет авторизацию и подписку
// We keep it here as it logic wrapper around AppLayout
import { useFirebaseAuth } from './contexts/FirebaseAuthContext';
import { FC } from 'react';

const SubscriptionGate: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, subscriptionActive, loading } = useFirebaseAuth();

  if (loading) return <LoadingSpinner />;

  // Если не вошел или нет подписки -> показываем стену.
  if (!user || !subscriptionActive) return <Paywall />;

  // ИНАЧЕ -> Возвращаем приложение
  return <>{children}</>;
};

const RoleGateWrapper: FC = () => {
  const { memberRole, loading, memberRecord, user, rolePolicies } = useFirebaseAuth();

  if (loading) return <LoadingSpinner />;

  return (
    <AuthProvider
      defaultRole={(memberRole as any) || 'user'}
      memberRecord={memberRecord}
      firebaseUser={user}
      rolePolicies={rolePolicies}
    >
      <Suspense fallback={<LoadingSpinner />}>
        <AppLayout />
      </Suspense>
      <CookieBanner />
    </AuthProvider>
  );
};

// Error Boundary
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Critical Render Error:", error, errorInfo);

    // Check for "Failed to fetch dynamically imported module"
    // This happens after a deployment when old hashes are gone
    const errorString = error?.toString() || '';
    if (errorString.includes('Failed to fetch dynamically imported module')) {
      const lastReload = sessionStorage.getItem('chunk_reload');
      const now = Date.now();

      // If we haven't reloaded in the last 10 seconds to fix this, do it now
      if (!lastReload || (now - parseInt(lastReload)) > 10000) {
        sessionStorage.setItem('chunk_reload', now.toString());
        console.warn('ChunkLoadError detected. Auto-reloading to fetch latest version...');
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-lg w-full text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Что-то пошло не так</h1>
            <p className="text-gray-600 mb-6 font-mono text-sm break-all">
              {this.state.error?.toString()}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              Перезагрузить приложение
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const App: FC = () => {
  useEffect(() => {
    // Client-side redirect for www to non-www
    if (window.location.hostname === 'www.pl-online.ru') {
      const newUrl = 'https://pl-online.ru' + window.location.pathname + window.location.search;
      window.location.replace(newUrl);
      return;
    }

    trackVisit();
    trackGoal('DB_CONNECTION'); // Send goal on app start
  }, []);

  const [waybillToOpen, setWaybillToOpen] = useState<string | null>(null);

  // Parse URL params for Firebase Auth action links (e.g., Reset Password)
  const urlParams = new URLSearchParams(window.location.search);
  const authMode = urlParams.get('mode');
  const authCode = urlParams.get('oobCode');

  if (authMode === 'resetPassword' && authCode) {
    return (
      <ResetPassword
        oobCode={authCode}
        onComplete={() => {
          // Redirect to home without params
          window.location.href = window.location.origin + window.location.pathname;
        }}
      />
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <FirebaseAuthProvider>
          {window.location.href.includes('/mobile') ? (
            <MobileRedirectHandler />
          ) : (
            <SubscriptionGate>
              <QueryClientProvider client={queryClient}>
                <QueryBusListener />
                <RoleGateWrapper />
              </QueryClientProvider>
            </SubscriptionGate>
          )}
        </FirebaseAuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
